import { useMutation } from '@tanstack/react-query';
import { useGetCustomer } from 'api/customer';
import { useAuth } from 'auth';
import { getBakerApiBaseUrl, getBakerEnv } from 'processing-status/activity-status-util';
import { v4 as uuid4 } from 'uuid';

/* baker API swagger docs: https://sturdy-pancake-3e913kw.pages.github.io/openapi.html */

const env = getBakerEnv();

/**
 * request bake for an activity following changes in the Editor.
 * failures reject so the caller can roll back its sent-state snapshot and retry —
 * a silently dropped bake leaves the activity unprocessed with no sign that anything went wrong
 *  */
export async function requestActivityBake(
    token: string,
    customerId: string,
    region: string,
    activityId: string,
    includeRequestId?: boolean,
) {
    const path = `/api/activity/${activityId}/RequestBake`;
    const url = getBakerApiBaseUrl(region, env) + path;

    const req = await fetch(url, {
        method: 'POST',
        // let the request outlive the page — bakes are also requested on beforeunload ('closedWindow'),
        // and without keepalive the browser cancels the fetch when the tab closes
        keepalive: true,
        headers: {
            Authorization: 'Bearer ' + token,
            CustomerId: customerId,
            ...(includeRequestId ? { 'x-request-id': uuid4() } : {}), // optional, used between services
        },
    });

    if (!req.ok) {
        throw new Error(`Error calling the Baker API (RequestBake) for activity: ${activityId}`);
    }

    return {}; // baker api responses have no body except 500's which we dont want to show to the user
}

// the required input variables for the mutate function
interface BakeRequestVariables {
    activityId: string;
    includeRequestId?: boolean;
}

export const useActivityBakeRequest = () => {
    const user = useGetCustomer();
    const auth = useAuth();

    // using useMutation directly
    return useMutation({
        mutationFn: async (variables: BakeRequestVariables) => {
            const token = await auth.getAccessToken();

            if (!token || !user.data) {
                throw new Error(
                    `Unable to request bake for activity ${variables.activityId}: missing auth or customer`,
                );
            }

            // call fetch function with variables from the mutate call
            return requestActivityBake(
                token,
                user.data.id + '',
                user.data.aws_region,
                variables.activityId,
                variables.includeRequestId,
            );
        },
    });
};
